import { ChartsService } from './charts.service';
import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { YearsService } from '../../@core/utils/years.service';
import { LAST_ROUTE_INFO } from './constants';
import { InsightFiltersService } from './insight-global-filters.service';
import { PerimetersService } from './perimeters.service';

export interface NotationObservableMap<T extends KPI.BaseComputedNotation | KPI.BaseComputedNotation[]> {
  [key: string]: Observable<T>;
}

export interface NotationLoadingMap {
  [key: string]: ReplaySubject<boolean>;
}

@Injectable()
export class NotationsService {

  private globalNotations: NotationObservableMap<KPI.GlobalComputedNotation> = {};
  private globalNotationsLoading: NotationLoadingMap = {};

  private superCategoriesNotations: NotationObservableMap<KPI.SuperCategoryComputedNotation[]> = {};
  private superCategoriesLoading: NotationLoadingMap = {};

  private categoriesNotations: NotationObservableMap<KPI.CategoryComputedNotation[]> = {};
  private categoriesLoading: NotationLoadingMap = {};

  private subCategoriesNotations: NotationObservableMap<KPI.SubCategoryComputedNotation[]> = {};
  private subCategoriesLoading: NotationLoadingMap = {};

  private assetTypesNotations: NotationObservableMap<KPI.AssetTypeComputedNotation[]> = {};
  private assetTypesNotationsLoading: NotationLoadingMap = {};

  private assetsNotations: NotationObservableMap<KPI.AssetComputedNotation[]> = {};
  private assetsNotationsLoading: NotationLoadingMap = {};

  private sitesNotations: NotationObservableMap<KPI.PerimeterComputedNotation[]> = {};
  private sitesNotationsLoading: NotationLoadingMap = {};

  private perimetersNotations: NotationObservableMap<KPI.PerimeterComputedNotation[]> = {};
  private perimetersNotationsLoading: NotationLoadingMap = {};

  private notationsRefresh$ = new BehaviorSubject<void>(null);

  public lastRouteInfo = {
    superCategory: null,
    showCategories: null,
    category: null,
    subCategory: null,
    assetType: null,
    asset: null,
    site: null,
    perimeter: null,
    showSuperCategories: false,
  };

  private lastRouteKey = LAST_ROUTE_INFO;

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
    private years: YearsService,
    private insightFiltersService: InsightFiltersService,
    private chartsService: ChartsService,
  ) { }

  public saveLastRouteInfo(currentUrl: string, lastRoute): void {

    const lastRoutesInfo = [];
    const lastRoutesInfoJSON = localStorage.getItem(this.lastRouteKey);
    if (lastRoutesInfoJSON) {
      const lastRoutesInfoParsed = JSON.parse(lastRoutesInfoJSON);
      lastRoutesInfo.push(...lastRoutesInfoParsed);
    }

    const routeIndex = lastRoutesInfo.findIndex(routeInfo => routeInfo['url'] === currentUrl);

    const routeToSave = {
      url: currentUrl,
      lastRoute: lastRoute,
    };

    if (routeIndex > -1) {
      lastRoutesInfo.splice(routeIndex, 1, routeToSave);
    } else {
      lastRoutesInfo.push(routeToSave);
    }

    localStorage.setItem(this.lastRouteKey, JSON.stringify(lastRoutesInfo));
  }

  public getLastRouteInfo(currentUrl: string): any {

    let lastRoutesInfoParsed: any;
    const lastRoutesInfoJSON = localStorage.getItem(this.lastRouteKey);
    if (lastRoutesInfoJSON) {
      lastRoutesInfoParsed = JSON.parse(lastRoutesInfoJSON);
      return lastRoutesInfoParsed.filter(lastRoute =>
        lastRoute['url'] === currentUrl,
      ).map(
        lastRoute => lastRoute['lastRoute'],
      )[0];
    } else {
      return {
        superCategory: null,
        showCategories: null,
        category: null,
        subCategory: null,
        assetType: null,
        asset: null,
        site: null,
        perimeter: null,
        showSuperCategories: false,
      };
    }

  }

  public static getEvolution(notation: KPI.BaseComputedNotation, startYear: number, endYear: number): number {
    const startNote = notation.notation[startYear].notation;
    const endNote = notation.notation[endYear].notation;

    return (endNote - startNote) / startNote * 100;
  }

  public refreshNotations(): void {
    this.notationsRefresh$.next();
  }

  public getGlobalNotation(
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
    filterInvestments: boolean = true,
  ): [Observable<KPI.GlobalComputedNotation>, Observable<boolean>] {
    const key = this.getKey(arguments);
    if (!this.globalNotations[key]) {
      this.globalNotationsLoading[key] = new ReplaySubject(1);

      this.globalNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
        this.chartsService._capexHasChanged$,
      ]).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.globalNotationsLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_global(
            {
              start_year: forcedYears ? forcedYears[0] : startYear,
              end_year: forcedYears ? forcedYears[1] : endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: filterInvestments && selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.globalNotationsLoading[key].next(false)),
          ),
        ),
        shareReplay(1),
      );
    }

    return [this.globalNotations[key], this.globalNotationsLoading[key]];
  }

  public getSuperCategoriesNotations(
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
  ): [Observable<KPI.SuperCategoryComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);
    if (!this.superCategoriesNotations[key]) {
      this.superCategoriesLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.SuperCategoryComputedNotation[] = [];

      this.superCategoriesNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.superCategoriesLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_super_categories(
            {
              start_year: forcedYears?.length ? forcedYears[0] : startYear,
              end_year: forcedYears?.length ? forcedYears[1] : endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.superCategoriesLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(
            accumulator.filter((elt) => !notations.some((notation) => notation.super_category === elt.super_category)),
            notations
          );
          return accumulator;
        }),
        shareReplay(1),
      );
    }
    return [this.superCategoriesNotations[key], this.superCategoriesLoading[key]];

    // const [notation$, loading$] = this.getGlobalNotation(
    //   scenario,
    //   questions,
    //   forcedPerimeters,
    // );
    //

    // const superCategories$ = notation$.pipe(
    //   switchMap((globalNotation) =>
    //     of(globalNotation.super_categories),
    //   ),
    // );
    //
    // return [superCategories$, loading$];
  }

  public getSuperCategoryNotation(
    superCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
  ): [Observable<KPI.SuperCategoryComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getSuperCategoriesNotations(
      scenario,
      questions,
      forcedPerimeters,
      forcedYears,
    );

    return [
      notations$.pipe(
        filter((notation) => notation !== undefined),
        map((notations) => {
          return notations.find((notation) => notation.super_category === superCategory);
        }),
      ),
      loading$,
    ];
  }

  public getCategoriesNotations(
    superCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
  ): [Observable<KPI.CategoryComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);
    if (!this.categoriesNotations[key]) {
      this.categoriesLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.CategoryComputedNotation[] = [];

      this.categoriesNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.categoriesLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_categories(
            superCategory,
            {
              start_year: forcedYears?.length ? forcedYears[0] : startYear,
              end_year: forcedYears?.length ? forcedYears[1] : endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.categoriesLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(
            accumulator.filter((elt) => !notations.some((notation) => notation.category === elt.category)),
            notations
          );
          return accumulator;
        }),
        shareReplay(1),
      );
    }
    return [this.categoriesNotations[key], this.categoriesLoading[key]];
    //
    //
    // const [notation$, loading$] = this.getGlobalNotation(
    //   scenario,
    //   questions,
    //   forcedPerimeters,
    // );
    //
    // const categories$ = notation$.pipe(
    //   switchMap((globalNotation) => {
    //     if (globalNotation && globalNotation.super_categories) {
    //       return of(globalNotation.super_categories
    //         .filter((sup) => sup.super_category === superCategory)[0].categories,
    //       );
    //     } else {
    //       return of([]);
    //     }
    //   }),
    // );
    //
    // return [categories$, loading$];
  }

  public getCategoryNotation(
    category: number,
    superCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
  ): [Observable<KPI.CategoryComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getCategoriesNotations(
      superCategory,
      scenario,
      questions,
      forcedPerimeters,
      forcedYears,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.category === category)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getSubCategoriesNotations(
    superCategory: number,
    category: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.SubCategoryComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);
    if (!this.subCategoriesNotations[key]) {
      this.subCategoriesLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.SubCategoryComputedNotation[] = [];
      this.subCategoriesNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.subCategoriesLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_sub_categories(
            superCategory,
            category,
            {
              start_year: startYear,
              end_year: endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.subCategoriesLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(
            accumulator.filter((elt) => !notations.some((notation) => notation.sub_category === elt.sub_category)),
            notations
          );
          return accumulator;
        }),
        shareReplay(1),
      );
    }
    return [this.subCategoriesNotations[key], this.subCategoriesLoading[key]];

    // const [notation$, loading$] = this.getGlobalNotation(
    //   scenario,
    //   questions,
    //   forcedPerimeters,
    // );
    // const subCategories$ = notation$.pipe(
    //   switchMap((globalNotation) => {
    //     if (!globalNotation || !globalNotation.super_categories) {
    //       return of([]);
    //     }
    //     const actualSuperCategory = globalNotation.super_categories.filter(
    //       (sup) => sup.super_category === superCategory)[0];
    //
    //     const actualCategory = actualSuperCategory.categories.filter(
    //       (cat) => cat.category === category)[0];
    //
    //     return of(actualCategory.sub_categories);
    //   }),
    // );
    //
    // return [subCategories$, loading$];
  }

  public getSubCategoryNotation(
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.SubCategoryComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getSubCategoriesNotations(
      superCategory,
      category,
      scenario,
      questions,
      forcedPerimeters,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.sub_category === subCategory)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getAssetTypesNotations(
    superCategory: number,
    category: number,
    subCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.AssetTypeComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.assetTypesNotations[key]) {
      this.assetTypesNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.AssetTypeComputedNotation[] = [];

      this.assetTypesNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.assetTypesNotationsLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_asset_types(
            superCategory,
            category,
            subCategory,
            {
              start_year: startYear,
              end_year: endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.assetTypesNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(
            accumulator.filter((elt) => !notations.some((notation) => notation.asset_type === elt.asset_type)),
            notations
          );
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.assetTypesNotations[key], this.assetTypesNotationsLoading[key]];
  }

  public getAssetTypeNotation(
    assetType: number,
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.AssetTypeComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getAssetTypesNotations(
      superCategory,
      category,
      subCategory,
      scenario,
      questions,
      forcedPerimeters,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.asset_type === assetType)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getAssetsNotations(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.AssetComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.assetsNotations[key]) {
      this.assetsNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.AssetComputedNotation[] = [];

      this.assetsNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.assetsNotationsLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_assets(
            superCategory,
            category,
            subCategory,
            assetType,
            {
              start_year: startYear,
              end_year: endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.assetsNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.assetsNotations[key], this.assetsNotationsLoading[key]];
  }

  public getAssetNotation(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    asset: number,
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.AssetComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getAssetsNotations(
      superCategory,
      category,
      subCategory,
      assetType,
      scenario,
      questions,
      forcedPerimeters,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.asset === asset)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getAllAssetsNotations(
    scenario: number,
    questions?: number[],
    forcedPerimeters?: KPI.Perimeter[],
    forcedYears?: [number, number],
  ): [Observable<KPI.AssetComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.assetsNotations[key]) {
      this.assetsNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.AssetComputedNotation[] = [];

      this.assetsNotations[key] = combineLatest([
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.assetsNotationsLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api.audit_notations_all_assets(
            {
              start_year: forcedYears?.length ? forcedYears[0] : startYear,
              end_year: forcedYears?.length ? forcedYears[1] : endYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          ).pipe(
            finalize(() => this.assetsNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.assetsNotations[key], this.assetsNotationsLoading[key]];
  }

  public getSitesNotations(
    superCategory: number,
    category: number,
    subCategory: number,
    scenario: number,
    questions?: number[],
  ): [Observable<KPI.PerimeterComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.sitesNotations[key]) {
      this.sitesNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.PerimeterComputedNotation[] = [];

      this.sitesNotations[key] = combineLatest([
        this.years.latestYears$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.sitesNotationsLoading[key].next(true)),
        switchMap(([[startYear, endYear]]) =>
          this.api.audit_notations_sites(
            superCategory,
            category,
            subCategory,
            {
              start_year: startYear,
              end_year: endYear,
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
            },
          ).pipe(
            finalize(() => this.sitesNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.sitesNotations[key], this.sitesNotationsLoading[key]];
  }

  public getSiteNotation(
    site: number,
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
    questions?: number[],
  ): [Observable<KPI.PerimeterComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getSitesNotations(
      superCategory,
      category,
      subCategory,
      scenario,
      questions,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.perimeter === site)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getPerimetersNotations(
    superCategory: number,
    category: number,
    subCategory: number,
    site: number,
    scenario: number,
    questions?: number[],
    lifecycle?: boolean,
  ): [Observable<KPI.PerimeterComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.perimetersNotations[key] || lifecycle) {
      this.perimetersNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.PerimeterComputedNotation[] = [];

      this.perimetersNotations[key] = combineLatest([
        this.years.latestYears$,
        this.notationsRefresh$,
      ]).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.perimetersNotationsLoading[key].next(true)),
        switchMap(([[startYear, endYear]]) =>
          this.api.audit_notations_perimeter(
            superCategory,
            category,
            subCategory,
            site,
            {
              start_year: lifecycle ? startYear - 5 : startYear,
              end_year: lifecycle ? endYear + 5 : endYear,
              scenario,
              ...(questions ? { questions: questions.join(',') } : {}),
            },
          ).pipe(
            finalize(() => this.perimetersNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.perimetersNotations[key], this.perimetersNotationsLoading[key]];
  }

  public getPerimeterNotation(
    perimeter: number,
    site: number,
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
    questions?: number[],
    lifecycle?: boolean,
  ): [Observable<KPI.PerimeterComputedNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getPerimetersNotations(
      superCategory,
      category,
      subCategory,
      site,
      scenario,
      questions,
      lifecycle,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.perimeter === perimeter)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }

  private getPerimetersObservable(perimeters?: KPI.Perimeter[]): Observable<KPI.Perimeter[]> {
    if (perimeters) {
      return of(perimeters).pipe(shareReplay(1));
    } else {
      return this.perimetersService.perimeters$;
    }
  }

  public getMissingNotationReasonLabel(value: KPI.MissingNotationReason): string {
    switch (value) {
      case KPI.MissingNotationReason.NOT_SET:
        return 'asset.notation_missing_reasons.unknown';
      case KPI.MissingNotationReason.STOPPED_ABNORMAL:
        return 'asset.notation_missing_reasons.stopped_equipment_abnormal';
      case KPI.MissingNotationReason.STOPPED_INSTRUCTION:
        return 'asset.notation_missing_reasons.stopped_equipment_instruction';
      case KPI.MissingNotationReason.BROKEN:
        return 'asset.notation_missing_reasons.broken_equipment';
      case KPI.MissingNotationReason.ACCESS:
        return 'asset.notation_missing_reasons.inaccessible_equipment';
      case KPI.MissingNotationReason.WAITING_EVALUATION:
        return 'asset.notation_missing_reasons.waiting_evaluation';
      case KPI.MissingNotationReason.IMPOSSIBLE:
        return 'asset.notation_missing_reasons.not_possible';
      case KPI.MissingNotationReason.MANAGER_UNAVAILABLE:
        return 'asset.notation_missing_reasons.manager_unavailable';
      case KPI.MissingNotationReason.MANAGER_DOESNT_KNOW:
        return 'asset.notation_missing_reasons.manager_doesnt_know';
      default:
        // should never occur
        return 'asset.notation_missing_reasons.unknown';
    }
  }

}
